<template>
  <div class="organization-health-row">
    <div class="organization-health-row-top">
      <span class="organization-health-row-label">{{ label }}</span>
      <span
        class="organization-health-row-percent"
        :class="{ 'organization-health-row-percent--no-data': percent == null }"
        v-text="percentLabel"
      ></span>
    </div>
    <div
      class="organization-health-bar"
      role="progressbar"
      :aria-label="label"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-valuenow="percent == null ? null : barValue"
      :aria-valuetext="percentLabel"
    >
      <div class="organization-health-bar-fill" :style="{ width: barWidth }"></div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

// One completeness row of the Organization health card (ER-12272): a label,
// the whole-number percentage returned by the endpoint, and a filled bar.
const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  // The endpoint returns `percent` as a whole number, or null when there is
  // nothing to measure (total === 0). The row renders the null case as "No data"
  // with an empty bar, never as a misleading 0%.
  percent: {
    type: Number,
    default: null,
  },
});

const percentLabel = computed(() => {
  if (props.percent == null) return "No data";
  return `${props.percent}%`;
});

const barValue = computed(() => {
  if (props.percent == null) return 0;
  return Math.min(Math.max(props.percent, 0), 100);
});

const barWidth = computed(() => `${barValue.value}%`);
</script>

<style scoped>
.organization-health-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.organization-health-row-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
}

.organization-health-row-label {
  color: var(--description-text, #475467);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
}

.organization-health-row-percent {
  color: var(--subheadings, #344054);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}

.organization-health-row-percent--no-data {
  color: var(--description-text, #475467);
  font-weight: 400;
}

.organization-health-bar {
  height: 8px;
  width: 100%;
  background: var(--stroke-not-selcted, #eaecf0);
  border-radius: 100px;
  overflow: hidden;
}

.organization-health-bar-fill {
  height: 100%;
  border-radius: 100px;
  background: var(--primary-normal, #7f56d9);
  transition: width 0.3s ease;
}
</style>
